import React, { ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { useBetterAuthConvexAuth } from '../../hooks/useAppAuth';
import UpdateDetailsModal from '../Verification/UpdateDetailsModal';

interface PasswordChangeGateProps {
  children: ReactNode;
}

/**
 * Gate component that blocks access if the user needs to change their password.
 * Shows a password change prompt and blocks navigation until the password is changed.
 *
 * With Better Auth, password changes are handled through the hosted AuthKit UI
 * or via a custom change-password flow.
 */
const PasswordChangeGate: React.FC<PasswordChangeGateProps> = ({ children }) => {
  const { convexUser, requiresPasswordChange, requiresContactVerification, isLoadingProfile } = useBetterAuthConvexAuth();
  const navigate = useNavigate();

  if (isLoadingProfile) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-green-500"></div>
      </div>
    );
  }

  if (requiresPasswordChange) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-gray-50 px-4">
        <div className="max-w-md w-full bg-white rounded-lg shadow-lg p-8">
          <div className="text-center mb-6">
            <div className="mx-auto h-16 w-16 flex items-center justify-center rounded-full bg-yellow-100 mb-4">
              <svg
                className="h-8 w-8 text-yellow-600"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
                />
              </svg>
            </div>
            <h2 className="text-2xl font-medium font-sans text-gray-900 mb-2">
              Password Change Required
            </h2>
            <p className="text-gray-600 mb-4">
              For security purposes, you need to update your password before continuing.
            </p>
            {convexUser?.passwordExpiresAt && convexUser.passwordExpiresAt < Date.now() && (
              <p className="text-sm text-yellow-600 mb-4">
                Your password has expired. Please set a new password.
              </p>
            )}
          </div>

          <div className="space-y-4">
            <p className="text-sm text-gray-500 text-center">
              You must change your password before continuing.
            </p>

            <div className="flex justify-center gap-3">
              <button
                onClick={() => navigate('/dashboard/change-password')}
                className="bg-[#16A34A]  text-white px-6 py-3 rounded-md hover:bg-shade-green-700 transition-colors"
              >
                Go to Change Password
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (requiresContactVerification) {
    return (
      <UpdateDetailsModal
        allowClose={false}
        onClose={() => undefined}
        onUpdate={() => {
          window.location.reload();
        }}
      />
    );
  }

  return <>{children}</>;
};

export default PasswordChangeGate;
